import {
	UntypedFormGroup,
	UntypedFormControl,
	Validators,
} from '@angular/forms';
import { BaseResponse } from './base-response.model';
import { Validation } from './validation.model';
import { startWith } from 'rxjs/operators';

export interface HousingResponse extends BaseResponse {
	message: string;
	housing: HousingJSON;
}

export interface HousingJSON {
	nuqHousing: string;
	address1: string;
	address2: string;
	address3: string;
	comments: string;
	qtel: string;
	qBuddy: string;
	housingUnitName: string;
	housingUnitId: number;
	housingUnitPhoneNumber: string;
	housingUnitKahramaaId: string;
	cgisUnit: string;
	cgisBuildingNumber: string;
	cgisStreet: string;
	cgisZone: string;
}

export class Housing {
	constructor(
		public nuqHousing: string,
		public address1: string,
		public address2: string,
		public address3: string,
		public comments: string,
		public qtel: string,
		public qBuddy: string,
		public housingUnitName: string,
		public housingUnitId: number,
		public housingUnitPhoneNumber: string,
		public housingUnitKahramaaId: string,
		public cgisUnit: string,
		public cgisBuildingNumber: string,
		public cgisStreet: string,
		public cgisZone: string
	) {}

	static fromJSON(json: HousingJSON): Housing {
		return Object.assign(Object.create(Housing.prototype), json, {});
	}

	static createFormGroup(
		housing: Housing,
		validation: Validation
	): UntypedFormGroup {
		const fg = new UntypedFormGroup({
			isNuqHousing: new UntypedFormControl(
				housing ? housing.isNuqHousing() : false
			),
			nuqHousing: new UntypedFormGroup({
				housingUnitId: new UntypedFormControl(
					housing ? housing.housingUnitId : null,
					[Validators.required]
				),
			}),
			otherHousing: new UntypedFormGroup({
				address1: new UntypedFormControl(
					housing ? housing.address1 : '',
					[
						Validators.maxLength(validation.comment.maxLength),
						Validators.pattern(validation.comment.pattern),
					]
				),
				address2: new UntypedFormControl(
					housing ? housing.address2 : '',
					[
						Validators.maxLength(validation.comment.maxLength),
						Validators.pattern(validation.comment.pattern),
					]
				),
				address3: new UntypedFormControl(
					housing ? housing.address3 : '',
					[
						Validators.maxLength(validation.comment.maxLength),
						Validators.pattern(validation.comment.pattern),
					]
				),
				qtel: new UntypedFormControl(housing ? housing.qtel : '', [
					Validators.maxLength(validation.comment.maxLength),
					Validators.pattern(validation.comment.pattern),
				]),
				cgisUnit: new UntypedFormControl(
					housing ? housing.cgisUnit : '',
					[
						Validators.maxLength(validation.alnum.maxLength),
						Validators.pattern(validation.alnum.pattern),
					]
				),
				cgisBuildingNumber: new UntypedFormControl(
					housing ? housing.cgisBuildingNumber : '',
					[
						Validators.maxLength(validation.alnum.maxLength),
						Validators.pattern(validation.alnum.pattern),
					]
				),
				cgisStreet: new UntypedFormControl(
					housing ? housing.cgisStreet : '',
					[
						Validators.maxLength(validation.alnum.maxLength),
						Validators.pattern(validation.alnum.pattern),
					]
				),
				cgisZone: new UntypedFormControl(
					housing ? housing.cgisZone : '',
					[
						Validators.maxLength(validation.alnum.maxLength),
						Validators.pattern(validation.alnum.pattern),
					]
				),
			}),
		});
		fg.get('isNuqHousing')
			.valueChanges.pipe(startWith(fg.get('isNuqHousing').value))
			.subscribe((isNuqHousing: boolean) =>
				this.processFormGroupVisibility(isNuqHousing, fg)
			);
		return fg;
	}

	static processFormGroupVisibility(
		isNuqHousing: boolean,
		fg: UntypedFormGroup
	): void {
		if (isNuqHousing) {
			fg.get('nuqHousing').enable();
			fg.get('otherHousing').disable();
		} else {
			fg.get('otherHousing').enable();
			fg.get('nuqHousing').disable();
		}
	}

	static fromFormGroup(fg: UntypedFormGroup): Housing {
		const h: UntypedFormGroup = fg.get('housing') as UntypedFormGroup;
		const values: any = h.getRawValue();
		const isNuqHousing: boolean = values['isNuqHousing'];
		return Object.assign(Object.create(Housing.prototype), {
			nuqHousing: isNuqHousing ? 'nuq' : 'other',
			housingUnitId: isNuqHousing
				? values['nuqHousing']['housingUnitId']
				: null,
			address1: isNuqHousing ? null : values['otherHousing']['address1'],
			address2: isNuqHousing ? null : values['otherHousing']['address2'],
			address3: isNuqHousing ? null : values['otherHousing']['address3'],
			qtel: isNuqHousing ? null : values['otherHousing']['qtel'],
			cgisUnit: isNuqHousing ? null : values['otherHousing']['cgisUnit'],
			cgisBuildingNumber: isNuqHousing
				? null
				: values['otherHousing']['cgisBuildingNumber'],
			cgisStreet: isNuqHousing
				? null
				: values['otherHousing']['cgisStreet'],
			cgisZone: isNuqHousing ? null : values['otherHousing']['cgisZone'],
		});
	}

	isNuqHousing(): boolean {
		return this.nuqHousing === 'nuq';
	}
}
